import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';

interface SearchResult {
  id: string;
  question: string;
  answer: string;
  category: string;
  tags: string[];
  relevanceScore: number;
  isBestMatch: boolean;
  isFaq: boolean;
  updatedAt: string;
}

interface SearchResponse {
  results: SearchResult[];
  totalCount: number;
  query: string;
  cached: boolean;
  suggestion?: string;
}

const styles: Record<string, React.CSSProperties> = {
  fab: {
    position: 'fixed',
    bottom: '96px',
    left: '24px',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#2196f3',
    color: 'white',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
    zIndex: 1050,
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1051,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '10vh',
  },
  panel: {
    width: '90%',
    maxWidth: '600px',
    maxHeight: '70vh',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem 1.25rem',
    borderBottom: '1px solid #e0e0e0',
    gap: '0.75rem',
  },
  input: {
    flex: 1,
    padding: '0.6rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.95rem',
    outline: 'none',
  },
  searchBtn: {
    padding: '0.6rem 1rem',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
    color: '#666',
    padding: '0.25rem',
  },
  results: {
    flex: 1,
    overflowY: 'auto',
    padding: '0.75rem 1.25rem',
  },
  resultItem: {
    padding: '0.75rem',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'background-color 0.1s',
  },
  bestMatchItem: {
    padding: '0.75rem',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    borderRadius: '6px',
    backgroundColor: '#e8f5e9',
    borderLeft: '3px solid #1a6b3c',
  },
  resultQuestion: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#333',
    margin: '0 0 0.25rem 0',
  },
  resultAnswer: {
    fontSize: '0.8rem',
    color: '#555',
    margin: 0,
    lineHeight: 1.4,
  },
  badge: {
    display: 'inline-block',
    padding: '0.15rem 0.4rem',
    borderRadius: '10px',
    backgroundColor: '#e8f5e9',
    color: '#1a6b3c',
    fontSize: '0.7rem',
    fontWeight: 500,
    marginTop: '0.35rem',
  },
  bestMatchBadge: {
    display: 'inline-block',
    padding: '0.15rem 0.4rem',
    borderRadius: '10px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    fontSize: '0.65rem',
    fontWeight: 600,
    marginRight: '0.5rem',
  },
  footer: {
    padding: '0.75rem 1.25rem',
    borderTop: '1px solid #e0e0e0',
    textAlign: 'center' as const,
  },
  footerLink: {
    color: '#1a6b3c',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    textDecoration: 'underline',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#999',
    fontSize: '0.85rem',
  },
  suggestion: {
    textAlign: 'center' as const,
    padding: '1rem',
    color: '#666',
    fontSize: '0.85rem',
  },
};

export function SearchWidget() {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus input when overlay opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setResults(null);
    setQuery('');
    setExpandedId(null);
    // Return focus
    if (previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const currentPage = location.pathname.replace('/', '') || 'home';
      const result = await apiClient<SearchResponse>('/knowledge/search', {
        method: 'POST',
        body: {
          query: query.trim(),
          context: {
            userRole: user?.role || 'Attendee',
            currentPage,
          },
        },
      });
      setResults(result);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleGoToKnowledge = () => {
    handleClose();
    navigate('/knowledge');
  };

  if (!isAuthenticated) return null;

  return (
    <>
      {/* Floating Action Button */}
      <button
        style={styles.fab}
        onClick={() => setIsOpen(true)}
        aria-label="Open knowledge search"
      >
        🔍
      </button>

      {/* Search Overlay */}
      {isOpen && (
        <div style={styles.overlay} onClick={handleOverlayClick}>
          <div style={styles.panel} ref={panelRef} role="dialog" aria-label="Knowledge search">
            {/* Header with input */}
            <div style={styles.header}>
              <input
                ref={inputRef}
                style={styles.input}
                type="text"
                placeholder="Search knowledge base..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button style={styles.searchBtn} onClick={handleSearch} disabled={loading}>
                {loading ? '...' : 'Search'}
              </button>
              <button style={styles.closeBtn} onClick={handleClose} aria-label="Close search">
                ✕
              </button>
            </div>

            {/* Results */}
            <div style={styles.results}>
              {!results && !loading && (
                <p style={styles.empty}>Type a question and press Enter or click Search.</p>
              )}
              {loading && <p style={styles.empty}>Searching...</p>}
              {results && results.results.length === 0 && (
                <div style={styles.suggestion}>
                  <p>No results found.</p>
                  {results.suggestion && <p>💡 {results.suggestion}</p>}
                </div>
              )}
              {results && results.results.map((r) => (
                <div
                  key={r.id}
                  style={r.isBestMatch ? styles.bestMatchItem : styles.resultItem}
                  onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                >
                  <div>
                    {r.isBestMatch && <span style={styles.bestMatchBadge}>⭐ Best Match</span>}
                    <p style={styles.resultQuestion}>{r.question}</p>
                  </div>
                  {expandedId === r.id ? (
                    <p style={styles.resultAnswer}>{r.answer}</p>
                  ) : (
                    <p style={styles.resultAnswer}>
                      {r.answer.length > 100 ? r.answer.slice(0, 100) + '...' : r.answer}
                    </p>
                  )}
                  <span style={styles.badge}>{r.category}</span>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={styles.footer}>
              <button style={styles.footerLink} onClick={handleGoToKnowledge}>
                Go to full Knowledge Base →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
